<!--
  Create a record over the listing it will appear in, instead of routing away to
  the `/new` page and back.

  The add PAGE's counterpart (views/service/TabulatorPages/PageServiceModelAdd.vue)
  builds the same store URL the same way and posts the same payload —
  BreadModelForm is the form in both places, in `add` action. Only where the
  form lives changes: on success this refreshes the table behind it rather than
  redirecting to it.

  Opened by a parent holding a ref: `open({ modelKey })`. Unlike the edit
  dialog it needs no store channel — the "+ New" button belongs to the page,
  not to an imperatively-mounted cell formatter.
-->
<template>
  <v-dialog
    v-model="isOpen"
    max-width="680"
    :persistent="apiStore.isLoading"
    content-class="mad-dialog"
    @click:outside="close"
  >
    <div v-if="isOpen" class="mad-modal">
      <div class="mad-modal__head">
        <div class="mad-modal__identity">
          <h2 class="mad-modal__title">New {{ model.name.singular }}</h2>
          <p class="mad-modal__subtitle">
            Fill in the details below to create a new
            {{ model.name.singular.toLowerCase() }}.
          </p>
        </div>
        <button class="mad-modal__close" title="Close" @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="mad-modal__body">
        <BreadModelForm
          ref="form"
          embedded
          action="add"
          :api="apiStore"
          :model="model"
        />
      </div>

      <div class="mad-modal__foot">
        <button
          class="mad-modal__btn mad-modal__btn--ghost"
          type="button"
          @click="close"
        >
          Cancel
        </button>
        <v-btn
          class="mad-modal__btn mad-modal__btn--primary"
          depressed
          :loading="apiStore.isLoading"
          @click="submit"
        >
          Create
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Api from "@/objects/api";
import BreadModelForm from "@/components/common/BreadModelForm.vue";
import Model from "@/objects/model";
import Service from "@/objects/service";

export default {
  name: "BreadModelAddDialog",
  components: {
    BreadModelForm,
  },
  data: () => ({
    apiStore: new Api(),
    model: new Model(),
    service: new Service(),
    isOpen: false,
  }),
  methods: {
    /**
     * `modelKey` is passed in rather than read from the route, so a page can
     * open this for a model it isn't itself listing (a child table).
     */
    open({ modelKey }) {
      this.model.getByKey(modelKey);
      if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

      // Not callbackReset(): despite the name that is Api's pre-flight hook and
      // sets isLoading TRUE, which through `:persistent` would open the dialog
      // spinning and refusing to close.
      this.apiStore.isError = false;
      this.apiStore.error = null;

      this.apiStore.setMethod("POST");
      this.apiStore.setUrl(this.storeUrl());
      this.apiStore.setCallbackCompleted(() => {
        this.isOpen = false;
        this.$emit("created", { modelKey: this.model.key });
        this.$store.dispatch("showMessage", {
          message: "Created successfully.",
          messageType: "success",
          timeout: 2000,
        });
      });

      this.isOpen = true;
    },
    close() {
      if (this.apiStore.isLoading) return;
      this.isOpen = false;
    },
    submit() {
      if (this.$refs.form) this.$refs.form.validate();
    },
    storeUrl() {
      if (this.model.apiUrls.apiStoreUrl != "") {
        return `${this.$service[this.service.key]}${
          this.model.apiUrls.apiStoreUrl
        }`;
      }
      return `${
        this.$service[this.service.key]
      }/v1/en/console/tabulator/model/${this.model.key}/add`;
    },
  },
};
</script>

<style scoped>
/* Same three tiers as BreadModelEditDialog: chrome on --app-card, the form area
   a step back on --app-surface, the field wells a step forward again. Every band
   is painted — a transparent one shows the overlay scrim through the panel.
   Token-only colour, so dark needs no companion block. */
.mad-modal {
  background: var(--app-card);
  font-family: var(--tbl-font);
}

.mad-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  background: var(--app-card);
  border-bottom: 1px solid var(--app-border);
}

.mad-modal__identity {
  min-width: 0;
}

.mad-modal__title {
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--app-text);
  margin: 0;
}

.mad-modal__subtitle {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--app-text-faint);
  margin: 3px 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mad-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--app-text-faint);
  transition: all 0.15s ease;
}

.mad-modal__close:hover {
  background: var(--app-hover);
  color: var(--app-text);
}

/* Capped so a model with many fields scrolls the FORM rather than growing the
   dialog past the viewport and pushing its own Create button off-screen. */
.mad-modal__body {
  padding: 22px 24px 26px;
  max-height: 60vh;
  overflow-y: auto;
  background: var(--app-surface);
}

.mad-modal__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--app-border);
  background: var(--app-card);
}

.mad-modal__btn {
  height: 38px;
  padding: 0 20px;
  border-radius: 10px;
  font-family: var(--tbl-font);
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
  transition: all 0.15s ease;
}

.mad-modal__btn--ghost {
  border: 1px solid var(--app-border-strong);
  background: transparent;
  color: var(--app-text-muted);
}

.mad-modal__btn--ghost:hover {
  background: var(--app-hover);
  color: var(--app-text);
}

.mad-modal__btn--primary.v-btn {
  background: var(--app-accent) !important;
  color: var(--app-accent-fg) !important;
  box-shadow: none;
}

.mad-modal__btn--primary.v-btn:hover {
  background: var(--app-accent-strong) !important;
}

@media (max-width: 599px) {
  .mad-modal__head,
  .mad-modal__foot {
    padding: 12px;
  }

  .mad-modal__body {
    padding: 14px 12px 18px;
    max-height: 66vh;
  }

  .mad-modal__btn {
    flex: 1;
  }
}
</style>

<style>
/* Unscoped: VDialog moves this content out of the component, and `content-class`
   lands on an element it owns rather than on our root, so a scoped selector
   never reaches either. */
.v-dialog.mad-dialog {
  border-radius: 14px;
  background: var(--app-card);
  border: 1px solid var(--app-border);
  box-shadow: 0 24px 60px var(--app-shadow-deep, rgba(0, 0, 0, 0.35));
}

/* Vuetify paints every sheet its built-in near-black in the dark theme; the
   embedded form's card is meant to be a wrapper, not a surface. */
.v-dialog.mad-dialog .v-card.bmf__card--embedded {
  background: transparent !important;
  box-shadow: none !important;
}
</style>
